import { getPendingTokens } from '../../services/orderApi.js';
import { fetchStockAlerts } from '../../services/inventoryApi.js';
import { fetchTables } from '../../services/tableApi.js';
import { fetchBranchDashboardData } from '../../services/branchApi.js';

export const createBranchDashboardSlice = (set, get) => ({
    pendingOrders: [],
    lowStockItems: [],
    branchTotals: { today: 0, week: 0, month: 0, allTime: 0 },
    totalExpenses: 0,
    topSellingProducts: [],
    activeTokens: 0,
    isDashboardLoading: false,
    dashboardError: null,
    dashboardFirstLoad: true,

    loadDashboardData: async () => {
        if (get().isDashboardLoading) return;
        if (!get().dashboardFirstLoad) return;
        
        set({ isDashboardLoading: true, dashboardError: null });

        try {
            const [dashboardData, tokensResponse, alertsResponse, tablesResponse] = await Promise.all([
                fetchBranchDashboardData(),
                getPendingTokens(),
                fetchStockAlerts(),
                fetchTables(),
            ]);

            const tokens = tokensResponse?.tokens || [];
            const alertsData = alertsResponse?.alerts || [];
            const tables = tablesResponse?.tables || [];

            const orders = tokens.map(token => ({
                id: token.id,
                token: token.token_no,
                orderTime: token.issued_at ? new Date(token.issued_at) : null,
                table: token.table_name || `Table ${token.table_id}`,
                items: token.items || [],
            }));

            const alerts = alertsData.map(alert => ({
                id: alert.id,
                name: alert.stock_item_name,
                sku: `SKU-${alert.stock_item_id}`,
                quantity: null,
                minstock: null,
                reorderqty: null,
                alertType: alert.alert_type,
            }));

            set({
                pendingOrders: orders,
                lowStockItems: alerts,
                branchTotals: dashboardData.totals || { today: 0, week: 0, month: 0, allTime: 0 },
                totalExpenses: dashboardData.totalExpenses || 0,
                topSellingProducts: dashboardData.topSellingProducts || [],
                activeTokens: dashboardData.activeTokens || 0,
                isDashboardLoading: false,
                dashboardFirstLoad: false,
            });

            // Load tables into the sales store
            if (get()?.setTables) {
                get().setTables(tables);
            }

        } catch (error) {
            console.error('Error loading dashboard data:', error);
            set({
                dashboardError: error?.message || 'Failed to load dashboard data',
                isDashboardLoading: false,
            });
        }
    },

    refreshDashboardData: async () => {
        if (get().isDashboardLoading) return;

        set({ isDashboardLoading: true, dashboardError: null });

        try {
            const [dashboardData, tokensResponse, alertsResponse, tablesResponse] = await Promise.all([
                fetchBranchDashboardData(),
                getPendingTokens(),
                fetchStockAlerts(),
                fetchTables(),
            ]);

            const tokens = tokensResponse?.tokens || [];
            const alertsData = alertsResponse?.alerts || [];
            const tables = tablesResponse?.tables || [];

            const orders = tokens.map(token => ({
                id: token.id,
                token: token.token_no,
                orderTime: token.issued_at ? new Date(token.issued_at) : null,
                table: token.table_name || `Table ${token.table_id}`,
                items: token.items || [],
            }));

            const alerts = alertsData.map(alert => ({
                id: alert.id,
                name: alert.stock_item_name,
                sku: `SKU-${alert.stock_item_id}`,
                quantity: null,
                minstock: null,
                reorderqty: null,
                alertType: alert.alert_type,
            }));

            set({
                pendingOrders: orders,
                lowStockItems: alerts,
                branchTotals: dashboardData.totals || { today: 0, week: 0, month: 0, allTime: 0 },
                totalExpenses: dashboardData.totalExpenses || 0,
                topSellingProducts: dashboardData.topSellingProducts || [],
                activeTokens: dashboardData.activeTokens || 0,
                isDashboardLoading: false,
            });

            // Load tables into the sales store
            if (get()?.setTables) {
                get().setTables(tables);
            }

        } catch (error) {
            console.error('Error refreshing dashboard data:', error);
            set({
                dashboardError: error?.message || 'Failed to refresh dashboard data',
                isDashboardLoading: false,
            });
        }
    },

    setPendingOrders: (orders) => set((state) => ({ 
        pendingOrders: typeof orders === 'function' ? orders(state.pendingOrders) : orders 
    })),
    setLowStockItems: (items) => set((state) => ({ 
        lowStockItems: typeof items === 'function' ? items(state.lowStockItems) : items 
    })),
});